import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AdminCXAPI, cxError } from '../../../lib/apiCX';
import type { CXDraft, CXDraftInput, CXOnboardingPlan } from '../../../types/cx';

const statusLabels = {
  complete: 'Complete',
  not_applicable: 'Not applicable',
  blocked: 'Blocked',
  in_progress: 'In progress',
  not_started: 'Not started',
  not_recorded: 'No status saved',
};

function OnboardingFocus({ plan }: { plan: CXOnboardingPlan }) {
  return (
    <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 1 }}>
      {plan.account_name && <Typography level="body-xs">{plan.account_name}</Typography>}
      <Typography level="title-sm">Suggested focus: {plan.focus?.step || 'Customer check-in'}</Typography>
      <Typography level="body-sm">{plan.reason}</Typography>
      <Typography level="body-sm">
        {plan.completed_count} complete · {plan.skipped_count} not applicable · {plan.remaining_count} still
        to review
      </Typography>
      <Box component="details">
        <Typography component="summary" level="body-sm" sx={{ cursor: 'pointer' }}>
          Review saved onboarding progress
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
          {plan.steps.map((step) => (
            <Typography component="li" level="body-sm" key={step.key}>
              {step.step} — {statusLabels[step.status]}
              {step.track === 'parallel' ? ' · Additional step' : ''}
            </Typography>
          ))}
        </Box>
        <Typography level="body-xs">
          Blocked work comes first, then work in progress, then your configured sequence and additional steps.
          {plan.updated_at
            ? ` Progress last saved ${new Date(plan.updated_at).toLocaleString()}.`
            : ' No progress has been saved yet.'}
        </Typography>
      </Box>
      <Typography level="body-xs">
        Based on automatically tracked account activity and saved manual overrides. Save any notes or
        status overrides in the Onboarding tab before refreshing this follow-up.
      </Typography>
    </Sheet>
  );
}

/** Generation never sends or changes a template; applying is a separate human decision. */
export function AIDraft({
  input,
  disabled,
  onApply,
  onBusyChange,
}: {
  input: CXDraftInput;
  disabled?: boolean;
  onApply?: (draft: CXDraft) => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState(input.purpose);
  const [mode, setMode] = useState<'custom' | 'onboarding'>(input.mode || 'custom');
  const [context, setContext] = useState('');
  const [booked, setBooked] = useState(false);
  const [draft, setDraft] = useState<CXDraft | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const account = { customer_id: input.customer_id, organization_id: input.organization_id };
  const plan = useQuery({
    queryKey: ['admin', 'cx', 'draft-plan', account],
    queryFn: () => AdminCXAPI.draftPlan(account),
    enabled: open && mode === 'onboarding',
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const generate = useMutation({
    mutationFn: () =>
      AdminCXAPI.draftMessage({
        ...input,
        mode,
        purpose: mode === 'onboarding' ? 'Help with the next onboarding step' : purpose,
        extra_context: context,
        meeting_booked: booked,
      }),
    onMutate: () => {
      onBusyChange?.(true);
      setCopied(false);
      setDraft(null);
    },
    onSuccess: setDraft,
    onSettled: () => onBusyChange?.(false),
  });
  const close = () => {
    if (!generate.isPending) setOpen(false);
  };
  return (
    <>
      <Button
        variant="outlined"
        size="sm"
        disabled={disabled}
        startDecorator={<AutoAwesomeOutlined />}
        onClick={() => {
          setDraft(null);
          generate.reset();
          setPurpose(input.purpose);
          setMode(input.mode || 'custom');
          setContext('');
          setBooked(false);
          setCopied(false);
          setCopyError(false);
          setOpen(true);
        }}
      >
        Create AI draft
      </Button>
      <Modal open={open} onClose={close}>
        <ModalDialog
          sx={{ width: 680, maxWidth: 'calc(100vw - 32px)', maxHeight: '90dvh', overflow: 'auto' }}
        >
          <ModalClose disabled={generate.isPending} />
          <DialogTitle sx={{ pr: 3 }}>
            Personalize a {input.channel === 'sms' ? 'text' : 'customer email'}
          </DialogTitle>
          <Typography level="body-sm">
            Draft from the customer’s latest saved onboarding progress, or choose another topic. Your team
            reviews the message before using it.
          </Typography>
          {input.channel === 'sms' && (
            <Typography level="body-sm">
              We add your first name and ApexOS introduction, and greet the recipient by name when their
              number matches a saved contact. Each draft offers friendly help with one clear next step.
            </Typography>
          )}
          <FormControl>
            <FormLabel>Message focus</FormLabel>
            <Select
              value={mode}
              disabled={generate.isPending}
              onChange={(_, value) => {
                if (value) {
                  setMode(value);
                  setDraft(null);
                  generate.reset();
                }
              }}
            >
              <Option value="onboarding">Next onboarding step — choose automatically</Option>
              <Option value="custom">Another message — choose a topic</Option>
            </Select>
          </FormControl>
          {mode === 'onboarding' &&
            (plan.isFetching ? (
              <LinearProgress aria-label="Loading saved onboarding progress" />
            ) : plan.error ? (
              <Alert color="danger">
                {cxError(plan.error)}{' '}
                <Button size="sm" onClick={() => plan.refetch()}>
                  Retry progress
                </Button>
              </Alert>
            ) : (
              (draft?.onboarding || plan.data) && <OnboardingFocus plan={(draft?.onboarding || plan.data)!} />
            ))}
          {mode === 'custom' && (
            <FormControl>
              <FormLabel>What should this message help with?</FormLabel>
              <Input
                value={purpose}
                onChange={(e) => {
                  setPurpose(e.target.value);
                  setDraft(null);
                }}
                disabled={generate.isPending}
                slotProps={{ input: { maxLength: 300 } }}
              />
            </FormControl>
          )}
          <FormControl>
            <FormLabel>Customer context to include (optional)</FormLabel>
            <Textarea
              minRows={2}
              value={context}
              onChange={(e) => {
                setContext(e.target.value);
                setDraft(null);
              }}
              disabled={generate.isPending}
              placeholder="They asked for help importing contacts from JobNimbus."
              slotProps={{ textarea: { maxLength: 2000 } }}
            />
            <FormHelperText>
              Add useful details from your conversation, such as what is holding up this step. Internal notes
              and conversations are not included automatically; include only customer-safe details.
            </FormHelperText>
          </FormControl>
          <Checkbox
            checked={booked}
            onChange={(e) => {
              setBooked(e.target.checked);
              setDraft(null);
            }}
            disabled={generate.isPending}
            label="A meeting is already booked — don't ask them to book again"
          />
          <Typography level="body-sm">
            Each draft aims for one helpful next step: a relevant question, a screenshare offer, or a booking
            invitation when appropriate.
          </Typography>
          {onApply && (
            <Typography level="body-xs">
              Your current message is kept until you choose Use draft in composer, which replaces it with the
              reviewed draft.
            </Typography>
          )}
          <Button
            loading={generate.isPending}
            disabled={mode === 'onboarding' ? plan.isFetching || !plan.data || !!plan.error : !purpose.trim()}
            onClick={() => generate.mutate()}
          >
            {draft ? 'Regenerate draft' : 'Generate draft for review'}
          </Button>
          {generate.error && <Alert color="danger">{cxError(generate.error)}</Alert>}
          {draft && (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Alert color="warning">
                Review the facts and next step before using this draft. Nothing has been sent or saved.
              </Alert>
              {input.channel === 'email' && (
                <FormControl>
                  <FormLabel>Draft subject</FormLabel>
                  <Input
                    value={draft.subject}
                    disabled={generate.isPending}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                    slotProps={{ input: { maxLength: 255 } }}
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Draft message</FormLabel>
                <Textarea
                  minRows={5}
                  value={draft.body}
                  disabled={generate.isPending}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  slotProps={{ textarea: { maxLength: input.channel === 'sms' ? 1600 : 5000 } }}
                />
              </FormControl>
              {onApply ? (
                <Button
                  disabled={
                    generate.isPending ||
                    !draft.body.trim() ||
                    (input.channel === 'email' && !draft.subject.trim())
                  }
                  onClick={() => {
                    onApply(draft);
                    setOpen(false);
                  }}
                >
                  Use draft in composer
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    disabled={generate.isPending}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          [draft.subject, draft.body].filter(Boolean).join('\n\n'),
                        );
                        setCopied(true);
                        setCopyError(false);
                      } catch {
                        setCopyError(true);
                      }
                    }}
                  >
                    Copy personalized draft
                  </Button>
                  {copied && (
                    <Typography role="status">
                      Copied. Paste into the account’s message composer and review before sending.
                    </Typography>
                  )}
                  {copyError && (
                    <Alert color="warning">Copy was unavailable. Select and copy the draft text above.</Alert>
                  )}
                  <Typography level="body-xs">
                    This is a draft for the selected customer. Your reusable template stays unchanged.
                  </Typography>
                </>
              )}
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </>
  );
}
